import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import type { KeyboardEventHandler } from "react";
import { useSearchParams } from "react-router-dom";
import { useDebounce } from "use-debounce";
import { useEffect, useState, useRef } from "react";
import { cmmsTheme } from "@setup/theme/cmms-theme";

type Props = {
	onKeyDown: KeyboardEventHandler<HTMLDivElement> | undefined;
};

export const SearchFilter = ({ onKeyDown }: Props) => {
	const [searchParams, setSearchParams] = useSearchParams();
	const search = searchParams.get("search") ?? "";

	const [localSearchText, setLocalSearchText] = useState(search);
	const [debouncedSearchText] = useDebounce(localSearchText, 400);
	const isInternalUpdate = useRef(false);

	// Only sync from URL to local when it's an external change (e.g., Reset button, navigation)
	// Skip when the change came from our own debounce update
	useEffect(() => {
		if (isInternalUpdate.current) {
			isInternalUpdate.current = false;
			return;
		}
		// Only sync if the URL value is different from what the user is typing
		// This handles external changes like Reset button or direct URL navigation
		if (search !== localSearchText && search !== debouncedSearchText) {
			setLocalSearchText(search);
		}
	}, [search]);

	useEffect(() => {
		if (debouncedSearchText !== search) {
			isInternalUpdate.current = true;
			const newParams = new URLSearchParams(searchParams);
			if (debouncedSearchText === "") {
				newParams.delete("search");
			} else {
				newParams.set("search", debouncedSearchText);
			}
			setSearchParams(newParams, { replace: true });
		}
	}, [debouncedSearchText, search, searchParams, setSearchParams]);

	const handleFilter = (e: any) => {
		setLocalSearchText(e.target.value);
	};

	return (
		<Box position="relative">
			<TextField
				style={{ borderTopLeftRadius: 3, borderTopRightRadius: 3 }}
				label="Search"
				variant="outlined"
				margin="dense"
				onKeyDown={onKeyDown}
				value={localSearchText}
				onChange={handleFilter}
				fullWidth
				autoComplete="off"
				slotProps={{
					input: {
						style: {
							paddingRight: 45,
							height: cmmsTheme.dimensions.standardInput,
							marginTop: cmmsTheme.spacing.inputOffset,
						},
					},
				}}
			/>
			<i
				className="fa fa-search"
				style={{
					position: "absolute",
					right: 17,
					top: 20,
					cursor: "pointer",
				}}
			/>
		</Box>
	);
};
